<script>
import {
    ColumnsDirective, ColumnDirective
} from '@syncfusion/ej2-vue-grids/src/grid/columns.directive';
import {
    AggregatesDirective, AggregateDirective
} from '@syncfusion/ej2-vue-grids/src/grid/aggregates.directive';
import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import { library } from "@common/library/library";
import { configStore } from "@common/configStore";
import { ddStore } from "@common/ddStore";

const controlComponent = defineAsyncComponent(() => import('@common/control/control.vue'));

const configEditorSettings = {
    gridSettings: {
        type: 'OBJECT',
        label: 'Grid Settings',
        id: 'gridSettings',
        default: {
            height: 600,
            paging: true,
            sorting: 'asc',
            grouping: false
        },
        properties: {
            height: {
                type: 'number',
                label: 'Height',
                helpText: 'Height helpText.',
                default: 'Something',
                id: 'height'
            },
            paging: {
                type: 'string',
                label: 'Paging',
                helpText: 'Paging helpText.',
                default: '',
                id: 'paging'
            },
            sorting: {
                type: 'string',
                label: 'Sorting',
                helpText: 'Sorting helpText.',
                default: null,
                id: 'sorting'
            },
            grouping: {
                type: 'string',
                label: 'Grouping',
                helpText: 'Grouping helpText.',
                default: null,
                id: 'grouping'
            }
        }
    },
    tableHeaders: {
        label: 'Table Headers',
        id: 'tableHeaders',
        type: 'ARRAY',
        default: [],
        helpText: 'Headers for the grid.',
        itemConfig: {
            itemType: 'object',
            primaryKey: 'text',
            permissions: {
                canMove: true,
                canDelete: true,
                allowAddItem: true
            },
            keys: {
                text: {
                    type: 'string',
                    default: 'text',
                    id: 'text',
                    label: 'Header Text',
                    helpText: 'help text.'
                },
                field: {
                    type: 'string',
                    default: 'cTitle',
                    id: 'field',
                    label: 'Data Field',
                    helpText: 'help text.'
                },
                width: {
                    type: 'string',
                    default: '500',
                    id: 'width',
                    label: 'Column Width',
                    helpText: 'help text.'
                },
                type: {
                    type: 'string',
                    default: 'string',
                    id: 'type',
                    label: 'Type',
                    helpText: 'Set the type.'
                },
                format: {
                    type: 'string',
                    default: 'Test',
                    id: 'format',
                    label: 'Format',
                    helpText: 'Set the format.'
                },
                columnTemplate: {
                    type: 'string',
                    default: '',
                    id: 'columnTemplate',
                    label: 'Column Template',
                    helpText: 'Provide a custom template for the column data.'
                },
                templateComponents: {
                    type: 'ARRAY',
                    id: 'templateComponents',
                    helpText: 'If you are using components in your template, define them here.',
                    label: 'Template Components',
                    itemConfig: {
                        itemType: 'object',
                        primaryKey: 'htmlTag',
                        permissions: {
                            canMove: true,
                            canDelete: true,
                            allowAddItem: true
                        },
                        keys: {
                            htmlTag: {
                                type: 'dropdown',
                                itemsSource: 'componentStore.getComponentPaths()',
                                fields: {
                                    text: 'htmlTag',
                                    value: 'htmlTag'
                                },
                                default: 'some-component',
                                id: 'htmlTag',
                                label: 'HTML Tag',
                                helpText: 'HTML Tag used to represent the component in your template.'
                            },
                            componentPath: {
                                type: 'dropdown',
                                itemsSource: 'componentStore.getComponentPaths()',
                                fields: {
                                    text: 'text',
                                    value: 'value'
                                },
                                default: '@common/someComponent.vue',
                                id: 'componentPath',
                                label: 'Component Path',
                                helpText: 'The component path relative to root in our code library (see developer).'
                            }
                        },
                        newItem: {
                            htmlTag: 'build-info',
                            componentPath: '@common/buildInfo.vue'
                        }
                    },
                }
            },
            newItem: {
                text: 'Column Label',
                field: '',
                width: '500'
            }
        }
    }
};

let gridMainConfig = {
    cId: "gridMain",
    cType: "gridMain",
    cTitle: "Main Grid",
    cTemplate: `<div v-if="config" :class="{'isMainGrid': isMainGrid, 'isForProfessions': isForProfessions}" :id="ref + '_wrapper'">
                    <div v-if="isMainGrid && !isForProfessions" id="gridControls">
                        <div class="grid-show-all">
                            <control v-if="gridControls['SHOWALL']" :name="'SHOWALL__' + psp" :label="gridControls['SHOWALL']"></control>
                        </div>
                        <div class="grid-settings">
                            <control v-if="gridControls['GRID_SET']" :name="'GRID_SET__' + psp" :label="gridControls['GRID_SET']"></control>
                        </div>
                        <div class="grid-sg-list">
                            <control v-if="gridControls['GRID_SAVED_LIST']" :name="'GRID_SAVED_LIST__' + psp" :placeholder="gridControls['GRID_SAVED_LIST']"
                            :items="grids"
                            :value="selectedGrid"
                            ></control>
                        </div>
                        <div class="grid-filters">
                            <control v-if="gridControls['FILTER-ACTION']" :name="'GRID_SEARCH__' + psp" :label="gridControls['FILTER-ACTION']"></control>
                        </div>
                        <div class="grid-filter-list">
                            <control
                                v-if="gridControls['FILTER_LIST']"
                                :name="'GRID_FILTERS_LIST__' + psp"
                                :placeholder="gridControls['FILTER_LIST']"
                                :items="filters"
                                :value="selectedFilter"
                            ></control>
                        </div>
                        <textarea :id="'clipboard__' + psp" :ref="'clipboard__' + psp" :value="clipboardValue"></textarea>
                    </div>
                    <div class="clearfix"></div>
                    <div v-if="psp === 14" id="professionsButtons"></div>
                    <div class="clearfix"></div>
                    <div v-if="isMainGrid && !isForProfessions">
                        <div v-if="gridControls['GRID_TYPE']" class="grid-type">
                            <control
                                :name="'GRID_TYPE__' + psp"
                                :items="gridTypes"
                                :value="selectedGridType"
                            ></control>
                        </div>
                        <div class="grid-navigator e-pagertemplatemessage">
                            {{totalRecordsCount > 0 ? ((currentPage - 1) * pageSize + 1) : 0}} to {{currentPage * pageSize > totalRecordsCount ? totalRecordsCount : currentPage * pageSize}} of {{totalRecordsCount}} {{itemsTitle}}
                            <span v-for="(count, tmpPsp) in rowCountAdditional">
                                | {{count}} of total {{count}} {{pspParentNames(tmpPsp)}}
                            </span>
                        </div>
                        <div v-if="!hidePager" class="grid-navigator control-section">
                            <span>
                                <span p="title">Page {{currentPage}} of {{totalPages}}</span>
                                <a class="grid-navigator" href="javascript:void(0)" @click="goToPage(1)" style="cursor: default;">
                                    <img v-if="currentPage === 1" :src="projectUrl + '/coordinator/xsl/images/sArrowForwardLeftDisabled.gif'" alt="Go to the first page">
                                    <img v-else :src="projectUrl + '/coordinator/xsl/images/sArrowForwardLeft.gif'" alt="Go to the first page">
                                </a>
                                <a class="grid-navigator" href="javascript:void(0)" @click="goToPage(currentPage - 1)" style="cursor: default;">
                                    <img v-if="currentPage === 1" :src="projectUrl + '/coordinator/xsl/images/sArrowLeftDisabled.gif'" alt="Go to previous page">
                                    <img v-else :src="projectUrl + '/coordinator/xsl/images/sArrowLeft.gif'" alt="Go to previous page">
                                </a>
                                <input :ref="'pageInput'" type="text" size="3" aria-label="page number" maxlength="10" class="grid-navigator gridEngineNavigator" @keyup.enter="goToPage" :value="currentPage">
                                <a class="grid-navigator" href="javascript:void(0)" @click="goToPage(currentPage + 1)" style="cursor: pointer;">
                                    <img v-if="currentPage === totalPages" :src="projectUrl + '/coordinator/xsl/images/sArrowRightDisabled.gif'" alt="Go to next page">
                                    <img v-else :src="projectUrl + '/coordinator/xsl/images/sArrowRight.gif'" alt="Go to next page">
                                </a>
                                <a class="grid-navigator" href="javascript:void(0)" @click="goToPage(totalPages)" style="cursor: pointer;">
                                    <img v-if="currentPage === totalPages" :src="projectUrl + '/coordinator/xsl/images/sArrowForwardRightDisabled.gif'" alt="Go to the last page">
                                    <img v-else :src="projectUrl + '/coordinator/xsl/images/sArrowForwardRight.gif'" alt="Go to the last page">
                                </a>
                            </span>
                        </div>
                    </div>
                    <div class="clearfix"></div>
                    <div :class="{'grid-wrapper': true}" v-if="!preparingData" style="display: grid;">
                        <div v-if="!reloadingData" v-for="(item, key) in recordsByGroups" :key="key">
                            <teleport :to='groupsTeleportTo[key]'>
                                <control :value="groupSelected[key]" @update:value="groupSelected[key] = $event;" :name="'GROUP_SELECT_ALL__' + key" :actionParams="key"></control>
                            </teleport>
                        </div>
                        <ejs-tooltip ref="gridCellTooltip" target=".e-rowcell" :beforeRender="beforeTooltipRender">
                            <ejs-grid :id="cId"
                                      :dataSource="records"
                                      :dataBound="dataBound"
                                      :rowDataBound="rowDataBound"
                                      :rowSelecting="rowSelecting"
                                      :height="gridSettings.height || height"
                                      :rowHeight="gridSettings.rowHeight || 36"
                                      :width="width || 'auto'"
                                      :allowPaging="gridSettings.paging"
                                      :allowSorting="gridSettings.sorting"
                                      :sortSettings="gridSettings.sortSettings"
                                      :allowGrouping="gridSettings.grouping"
                                      :groupSettings='gridSettings.groupSettings'
                                      :allowMultiSorting="true"
                                      :allowFiltering="gridSettings.filtering"
                                      :allowResizing="gridSettings.resizing"
                                      :allowReordering="gridSettings.reordering"
                                      :toolbar="gridSettings.toolbar"
                                      :allowExcelExport="gridSettings.allowExcelExport"
                                      :rowSelected="rowSelected"
                                      :rowDeselected="rowSelected"
                                      :pageSettings="pageSettings"
                                      :clipMode="'Ellipsis'"
                                      :contextMenuItems="ctxMenu"
                                      :contextMenuOpen="contextMenuOpen"
                                      :contextMenuClick="contextMenuClick"
                                      :recordDoubleClick="recordDoubleClick"
                                      :actionBegin="actionBegin"
                                      :resizeStart="resizeStart"
                                      :resizeStop="resizeStop"
                                      :columnDragStart="columnDragStart"
                                      :columnDrop="columnDrop"
                                      :allowRowDragAndDrop="allowRowDragAndDrop"
                                      :allowSelection="true"
                                      :childGrid="gridSettings.childGrid"
                                      :toolbarTemplate="gridSettings.toolbarTemplate"
                                      :ref="ref">
                                <e-columns>
                                    <e-column v-for="(item, key) in headers"
                                              :key="key" :field="item.field"
                                              :customAttributes="item.customAttributes || {}"
                                              :headerText="item.text"
                                              :width="item.width"
                                              :minWidth="item.minWidth"
                                              :maxWidth="item.maxWidth"
                                              :type="item.type || null"
                                              :format="item.format || null"
                                              :showInColumnChooser="typeof item.showInColumnChooser !== 'undefined' ? item.showInColumnChooser : true"
                                              :visible="typeof item.visible !== 'undefined' ? item.visible : true"
                                              :allowSorting="item.allowSorting === false ? false : (item.isSortable === 0 ? false : true)"
                                              :allowFiltering="item.isSortable === 0 ? false : true"
                                              :allowGrouping="item.isSortable === 0 ? false : true"
                                              :filterTemplate="filterTemplates && filterTemplates[item.field]"
                                              :filter="item.filter || null"
                                              :sortComparer='sortComparer'
                                              :template="columnTemplates && columnTemplates[item.field]"></e-column>
                                </e-columns>
                                <e-aggregates v-if="gridSettings.grouping">
                                    <e-aggregate v-for="(item, key) in aggregates" :key="key">
                                        <e-columns>
                                            <e-column
                                                :field="item.field"
                                                :type="'Custom'"
                                                :customAggregate="item.customAggregateFn"
                                                :groupCaptionTemplate="item.groupCaptionTemplate"></e-column>
                                        </e-columns>
                                    </e-aggregate>
                                </e-aggregates>
                            </ejs-grid>
                        </ejs-tooltip>
                        <div v-if="this.gridInfo.SUMMARY_MODE" class="summaryGrid" :style="{ 'margin-top': this.summaryInfoMarginTop }">
                            <div class="infoTitle"><span>Summary info</span></div>
                            <div class="infoRow">
                                <div v-for="(item, key) in headers" class="infoCol">
                                    <div :id="key !== 0 ? ('summary_' + item.field) : ''" :style="{ 'width': (parseFloat(item.width) + (key === 0 && typeof this.groupingColumns === 'object' && Object.keys(this.groupingColumns).length !== 0 ? 20 : 0)) + 'px' }">
                                        {{key !== 0 ? (typeof this.data.summary !== 'undefined' && typeof this.data.summary[item.field] !== 'undefined' && this.data.summary[item.field]?.length === 2 ? this.data.summary[item.field][0] : 'N/A') : ''}}
                                    </div>
                                </div>
                            </div>

                            <div class="infoTitle"><span>Average info</span></div>
                            <div class="infoRow">
                                <div v-for="(item, key) in headers" class="infoCol">
                                    <div :id="key !== 0 ? ('avarage_' + item.field) : ''" :style="{ 'width': (parseFloat(item.width) + (key === 0 && typeof this.groupingColumns === 'object' && Object.keys(this.groupingColumns).length !== 0 ? 20 : 0)) + 'px' }">
                                        {{key !== 0 ? (typeof this.data.summary !== 'undefined' && typeof this.data.summary[item.field] !== 'undefined' && this.data.summary[item.field]?.length === 2 ? this.data.summary[item.field][1] : 'N/A') : ''}}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>`,
    pageSettings: {
        pageSize: 50,
        pageCount: 3
    },
    gridSettings: {
        paging: true,
        sorting: false,
        filtering: true,
        grouping: false
    },
    dsId: {
        type: 'dropdown',
        placeholder: "Select a dataset",
        onchange: "selectDataset",
        filterBarPlaceholder: "Search here",
        popupHeight: "220px",
        allowFiltering: true,
        default: false,
        fields: { text: 'cTitle', value: 'dsId' }
    },
    controls: [
        {
            name: 'SHOWALL',
            type: 'button',
            label: 'Show All',
            onclick: 'showAll'
        },
        {
            name: 'GRID_SET',
            type: 'button',
            label: 'Grid Settings',
            onclick: 'openGridSettings'
        },
        {
            name: 'GRID_SEARCH',
            type: 'button',
            label: 'Filters',
            onclick: 'openFilters'
        },
        {
            name: 'GRID_SAVED_LIST',
            type: 'dropdown',
            onchange: 'gridChange',
            noRecordsTemplate: 'No saved grids found',
            placeholder: 'Start typing a grid name...',
            allowFiltering: true,
            fields: { text: 'text', value: 'value' }
        },
        {
            name: 'GRID_FILTERS_LIST',
            type: 'dropdown',
            onchange: 'filterChange',
            noRecordsTemplate: 'No filters found',
            placeholder: 'Start typing a filter name...',
            allowFiltering: true,
            fields: { text: 'text', value: 'value' }
        },
        {
            name: 'GRID_TYPE',
            type: 'dropdown',
            onchange: 'gridTypeChange',
            showClearButton: false,
            fields: { text: 'text', value: 'value' }
        },
        {
            name: 'GROUP_SELECT_ALL',
            type: 'checkbox',
            onchange: 'subGroupSelect'
        }
    ]
};

const generateColumnTemplate = (template) => () => ({
    template: vueApp.component('gridColumnTemplate', {
        template: template,
        components: {
            control: controlComponent
        }
    }),
    data() {
        return { data: {} };
    }
});

let gridMainCmp = {
    editorSettings: configEditorSettings,
    name: 'gridMain',
    preInitControls: true,
    props: {
        data: {
            default: []
        },
        settings: {
            default: false
        },
        psp: {
            default: 1
        },
        fromPsp: {
            default: 1
        },
        selectedItems: {
            default: []
        },
        selectedItemsFullData: {
            default: []
        },
        selectedFolder: {
            default: false
        },
        loadFunc: {
            required: false
        },
        customCtxMenu: {
            default: false
        },
        ctxMenuOpenFnc: {
            default: false
        },
        ctxClickFnc: {
            default: false
        },
        dblClickFnc: {
            default: false
        },
        changePageFnc: {
            default: false
        },
        resizeStopFnc: {
            default: false
        },
        columnDropFnc: {
            default: false
        },
        searchFnc: {
            default: false
        },
        detailDataBoundFnc: {
            default: false
        },
        columnsVisibilityChangeFnc: {
            default: false
        },
        isList: {
            default: false
        },
        rowBoundFunc: {
            required: false
        },
        rowSelectingFunc: {
            required: false
        },
        fromMainGrid: {
            default: false
        }
    },
    data() {
        return {
            records: false,
            summary: false,
            headers: false,
            aggregates: [],
            columnTemplates: {},
            filterTemplates: false,
            tooltipContent: '',
            preparingData: true,
            reloadingData: false,
            width: 'auto',
            height: false,
            oldHeight: false,
            summaryInfoMarginTop: '0px',
            filterValue: {},
            initialGridLoad: true,
            ctxMenu: [],
            gridControls: [],
            gridData: [],
            selectedGrid: false,
            selectedFilter: null,
            selectedGridType: null,
            currentPage: 1,
            totalPages: 0,
            pageSize: 100,
            hidePager: false,
            rowCountAdditional: {},
            totalRecordsCount: 0,
            filters: [],
            grids: [],
            gridTypes: [
                { text: 'Any Affiliation', value: -1 },
                { text: 'DD.COMMON.ENTITY_AFFILIATION_VOL_ORG', value: 0 },
                { text: 'DD.COMMON.ENTITY_AFFILIATION_VOL_ASSIGNMENT', value: 1 },
                { text: 'DD.COMMON.ENTITY_AFFILIATION_VOL_AE', value: 2 },
                { text: 'DD.COMMON.ENTITY_AFFILIATION_ORG_ORG', value: 3 },
                // { text: 'DD.COMMON.ENTITY_AFFILIATION_VOL_OPP', value: 4 }, /* VMS-9994 */
            ],
            clipboardValue: '',
            leftMenu: [],
            reloadButtons: false,
            columnRemoved: false,
            groupingColumns: {},
            prevSelectedItems: [],
            sortingColumns: [],
            groupsTeleportTo: {},
            recordsByGroups: {},
            groupSelected: {}
        };
    },
    computed: {
        gridSettings() {
            let settings = library.cloneObj(this.settings);
            if ((!settings || library.objIsEmpty(settings)) && this.config && this.config.gridSettings) {
                settings = library.cloneObj(this.config.gridSettings);
            }
            return settings;
        },
        projectUrl() {
            let projectUrl = '/';
            if (typeof PROJECTS_URL !== 'undefined') projectUrl = PROJECTS_URL;
            return projectUrl;
        },
        ref() {
            return this.cId;
        },
        isMainGrid() {
            return !!(this.config && this.gridSettings && this.gridSettings.isMainGrid);
        },
        itemsTitle() {
            return configStore.pspParentNamesPlural[this.psp];
        },
        pageSettings() {
            let settings = library.cloneObj(this.settings);
            let pageSettings = settings && settings.pageSettings ? library.cloneObj(settings.pageSettings) : library.cloneObj(this.config.pageSettings);
            pageSettings.currentPage = this.currentPage;
            if (this.isMainGrid && pageSettings.pageSize !== this.pageSize) {
                pageSettings.pageSize = this.pageSize;
            }
            return pageSettings;
        },
        isForProfessions() {
            return this.fromPsp === 14;
        },
        allowRowDragAndDrop() {
            return (this.config && this.gridSettings && this.gridSettings.allowRowDragAndDrop) || this.psp === 14 || this.fromPsp === 14;
        },
        gridInfo() {
            let gridInfo = {};
            if (store.state.configStore.cfg.gridSettings
                && store.state.configStore.cfg.gridSettings[this.psp]
                && store.state.configStore.cfg.gridSettings[this.psp][this.selectedGrid]
            ) {
                gridInfo = store.state.configStore.cfg.gridSettings[this.psp][this.selectedGrid];
            }
            return gridInfo;
        }
    },
    watch: {
        // not need for now, do not remove
        /* 'propSelectedFolder': function (newValue, prevValue) {
            if (prevValue === false || newValue === false) return;
            if (!library.isEqualObj(newValue, prevValue)) {
                this.onFolderChange(newValue.DATA_VIEW_ID);
            }
        } */
    },
    methods: {
        beforeTooltipRender(args) {
            if (args?.target?.classList?.contains('e-rowcell') && args.target.innerText) {
                let elWithRealWidth = args.target.querySelectorAll('.cell-real-width')[0];
                if (elWithRealWidth && elWithRealWidth.offsetWidth > args.target.clientWidth) {
                    this.$refs['gridCellTooltip'].ej2Instances.content = args.target.innerHTML.replaceAll('\n', '<br/>');
                } else {
                    args.cancel = true;
                }
            } else {
                args.cancel = true;
            }
        },
        sortComparer() {
            return 0;
        },
        detailDataBound(e) {
            if (typeof this.detailDataBoundFnc === 'function') this.detailDataBoundFnc(e);
        },
        renderHeaderGroup() {
            if (typeof this.$refs?.[this.ref]?.$el?.querySelectorAll === 'function') {
                let groupArea = this.$refs[this.ref].$el.querySelectorAll('.e-groupdroparea');
                let gridHeader = this.$refs[this.ref].$el.querySelectorAll('.e-gridheader .e-headercontent');
                if (typeof groupArea?.[0].style !== 'undefined'
                    && typeof gridHeader?.[0].style !== 'undefined'
                ) {
                    let groupAreaStyles = getComputedStyle(groupArea[0]);
                    let gridHeaderStyles = getComputedStyle(gridHeader[0]);
                    if (groupAreaStyles && gridHeaderStyles && groupAreaStyles.width !== gridHeaderStyles.width) {
                        groupArea[0].style.width = gridHeaderStyles.width;
                    }
                }

                let headerGroupCells = this.$refs[this.ref].$el.querySelectorAll('.e-columnheader .e-grouptopleftcell');
                let alreadyAdded = this.$refs[this.ref].$el.querySelectorAll('.e-columnheader .group-collapse-all');
                if (typeof headerGroupCells?.[0]?.append === 'function' && !alreadyAdded.length) {
                    headerGroupCells[0].classList.add('e-grouptopleftcell-group-ungroup-all');
                    headerGroupCells[0].classList.add('expanded');
                    let collapseLink = document.createElement('a');
                    collapseLink.onclick = () => {
                        headerGroupCells[0].classList.remove('expanded');
                        headerGroupCells[0].classList.add('collapsed');
                        this.$refs[this.ref].groupCollapseAll();
                    };
                    collapseLink.title = "Expanded";
                    collapseLink.href = '#';
                    collapseLink.className = 'e-icons e-gdiagonaldown e-icon-gdownarrow group-collapse-all';
                    headerGroupCells[0].append(collapseLink);

                    let expandLink = document.createElement('a');
                    expandLink.onclick = () => {
                        headerGroupCells[0].classList.remove('collapsed');
                        headerGroupCells[0].classList.add('expanded');
                        this.$refs[this.ref].groupExpandAll();
                    };
                    expandLink.title = "Collapsed";
                    expandLink.href = '#';
                    expandLink.className = 'e-icons e-gnextforward e-icon-grightarrow group-expand-all';

                    headerGroupCells[0].append(expandLink);
                }
            }
        },
        getRecordsByGroups() {
            this.groupSelected = {};
            if (this?.gridSettings?.grouping && typeof this?.$refs?.[this.ref]?.ej2Instances?.getRowsObject === 'function') {
                let rowsObject = this.$refs[this.ref].ej2Instances.getRowsObject();
                let groups = {};
                let groupsTeleportTo = {};
                let currentNested = [];
                let prevIsData = false;
                let groupId = 0;
                rowsObject.forEach((record, i) => {
                    if (!record.isDataRow) {
                        if (prevIsData) {
                            if (record.indent >= 0) {
                                while (currentNested.length !== record.indent) {
                                    currentNested.pop();
                                }
                            }
                            prevIsData = false;
                        }
                        currentNested.push(record.data.key);
                        let currentGroupKey = currentNested.join('__,__');
                        groupsTeleportTo[currentGroupKey] = '#gridMain_mainGrid__AFFcaptioncell' + groupId + ' .group-checkbox';
                        groupId++;
                    } else {
                        prevIsData = true;
                        for (let tmpI in currentNested) {
                            let currentGroupKey = currentNested.slice(0, (parseInt(tmpI, 10) + 1)).join('__,__');
                            if (!Array.isArray(groups[currentGroupKey])) groups[currentGroupKey] = [];
                            groups[currentGroupKey].push(record.index);
                        }
                    }
                });
                this.groupsTeleportTo = library.cloneObj(groupsTeleportTo);
                this.recordsByGroups = library.cloneObj(groups);
            }
        },
        subGroupSelect(ev, el, groupKey) {
            if (
                Array.isArray(this.recordsByGroups[groupKey])
                && this.recordsByGroups[groupKey].length
                && typeof this?.$refs?.[this.ref]?.ej2Instances?.getSelectedRowIndexes === 'function'
            ) {
                let prevSelected = library.cloneObj(this.$refs[this.ref].ej2Instances.getSelectedRowIndexes());
                this.$refs[this.ref].ej2Instances.clearRowSelection();
                let selectRows = [];
                if (ev.checked) {
                    selectRows = [...prevSelected, ...this.recordsByGroups[groupKey]];
                } else {
                    selectRows = prevSelected.filter((tmp) => !this.recordsByGroups[groupKey].includes(tmp));
                }
                if (Array.isArray(selectRows) && selectRows.length) this.$refs[this.ref].ej2Instances.selectRows(selectRows);
            }
        },
        dataBound() {
            this.renderHeaderGroup();
            this.selectPrevious();
            this.getRecordsByGroups();
            if (this.fromMainGrid) this.resizeHeight();
            this.$emit('update:dataBound', true);
        },
        rowDataBound(args) {
            if (typeof this.rowBoundFunc === 'function') this.rowBoundFunc(this, args);
        },
        rowSelecting(args) {
            if (typeof this.rowSelectingFunc === 'function') this.rowSelectingFunc(this, args);
        },
        // not need for now, do not remove
        /* async onFolderChange(folderID) {
            let gridInfo = library.cloneObj(this.gridInfo);
            if (gridInfo.CLIENT_GRID_SAVED_ID && gridInfo.DATA_VIEW_ID) {
                gridInfo.CLIENT_GRID_SAVED_ID = null;
                delete gridInfo.CLIENT_GRID_SAVED_NAME;
                this.selectedGrid = null;
                this.selectedFilter = null;
            }
            gridInfo.DATA_VIEW_ID = folderID;
            delete gridInfo.DATA_VIEW_NAME;
            library.showLoadingIndicator(this.cId + '_wrapper');
            await configStore.setGridSettings(this.psp, gridInfo);
            library.hideLoadingIndicator(this.cId + '_wrapper');
            this.$nextTick(() => {
                this.getGridData(true);
            });
        }, */
        pspParentNames(psp) {
            return configStore.pspParentNamesPlural[psp];
        },
        onLoad() {
            if (typeof this.loadFunc === 'function' && !this.isMainGrid) this.loadFunc(this);
            if (this.config && this.gridSettings.heightToContent && this.$parent && this.$parent.$el && this.$parent.$el.parentElement) {
                window.addEventListener('resize', () => {
                    this.resizeHeight();
                }, true);
                this.resizeHeight();
            }
            if (this.isMainGrid) {
                ddStore.preloadGroup('COMMON');
                ddStore.preloadGroup('VOLUNTEER');
                ddStore.preloadGroup('DATA_GRID');
                this.getMainGridData();
            }
        },
        resizeHeight() {
            let minusHeight = this.psp === 14 ? 300 : 160; // NOSONAR
            let tmpHeight = this.$parent.$el.parentElement.clientHeight - minusHeight;
            if (this.fromMainGrid && typeof this?.$parent?.$el?.querySelectorAll !== 'undefined' && this.$parent.$el.querySelectorAll('.e-gridcontent').length) {
                tmpHeight = document.body.clientHeight - this.$parent.$el.querySelectorAll('.e-gridcontent')[0].getBoundingClientRect().top - 25; // NOSONAR
            }
            if (this.config && this.gridSettings.minHeight && tmpHeight < this.gridSettings.minHeight) {
                tmpHeight = this.gridSettings.minHeight;
            }

            this.height = tmpHeight;
            this.summaryInfoMarginTop = '0px';

            if (this.gridInfo.SUMMARY_MODE && this.$parent.$el.querySelector('.e-gridcontent .e-table')?.clientHeight) {
                if (!this.oldHeight && this.height && this.height > 500 && this.$parent.$el.querySelector('.summaryGrid')?.clientHeight) {
                    this.oldHeight = this.height;
                    this.height -= this.$parent.$el.querySelector('.summaryGrid')?.clientHeight;
                    tmpHeight = this.height;
                }

                let tableHeight = this.$parent.$el.querySelector('.e-gridcontent .e-table').clientHeight;

                if (tableHeight < tmpHeight) {
                    this.summaryInfoMarginTop = (tableHeight - tmpHeight + 10) + 'px';
                }
            } else if (this.oldHeight && this.height) {
                this.height = this.oldHeight;
                this.oldHeight = false;
            }
        },
        prepareData() {
            this.preparingData = true;
            this.$emit('update:preparingData', true);
            let tmpRecords;
            let tmpSummary;
            let tmpHeaders;
            let getHeadersFromSampleRecord = (iRecords, fromProp) => {
                let iHeaders = [];
                let width = this.gridSettings.width || 'auto';
                if (this.gridSettings.calculateWidth) {
                    width = 10;
                }
                let seachFunction = async () => {
                    if (this.isMainGrid) {
                        if (this.selectedGrid) await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': null });
                        this.gridSearch();
                    }
                    if (typeof this.searchFnc === 'function') this.searchFnc(this.filterValue);
                };
                let tmpFilterTemplates = {};
                this.filterTemplates = {};
                if (this.gridSettings.grouping && this.gridSettings.calculateWidth) {
                    width += 100;
                }
                if (this.gridSettings.enableCheckBox) {
                    if (width === 'auto') width = 0;
                    if (this.gridSettings.calculateWidth) {
                        width += 50;
                    }
                    iHeaders.push({
                        field: 'SEARCH',
                        text: '',
                        width: '40px',
                        minWidth: '40px',
                        maxWidth: '40px',
                        type: 'checkbox',
                        showInColumnChooser: false,
                        customAttributes: {
                            class: 'custom-search-column'
                        }
                    });
                    let tmpTemplateName = this.cId + "_filter__template" + library.getUniqueID();
                    let template = library.createTemplateComponent(tmpTemplateName,
                        {
                            template: `<div>
                                            <control :name="'SEARCH__' + tmpTemplateName" :prop-config="{...controlConfig, ...{onclick: click}}"></control>
                                        </div>`,
                            components: {
                                control: controlComponent
                            },
                            data() {
                                return {
                                    data: {},
                                    tmpTemplateName: tmpTemplateName,
                                    controlName: 'SEARCH',
                                    controlConfig: {
                                        name: 'SEARCH',
                                        type: 'button',
                                        label: 'Find'
                                    },
                                    searchFunction: seachFunction
                                };
                            },
                            methods: {
                                click(event) {
                                    seachFunction();
                                }
                            }
                        }, this);
                    tmpFilterTemplates['SEARCH'] = template;
                }
                if (fromProp) {
                    for (let i in iRecords) {
                        if (i === 'length') continue;
                        if (iRecords[i].width) {
                            if (width === 'auto') width = 0;
                            if (this.gridSettings.calculateWidth) {
                                width += iRecords[i].width;
                            }
                        }
                        if (this.isMainGrid && !iRecords[i].columnTemplate) {
                            iRecords[i].columnTemplate = '<div><span class="cell-real-width" v-html="data[\'' + iRecords[i].GRID_FIELD_NAME + '\']"></span></div>';
                        }
                        if (iRecords[i].columnTemplate) {
                            if (!this.columnTemplates) this.columnTemplates = {};
                            let template = library.createTemplateComponent(this.cId + "_column_" + i + "_template",
                                {
                                    template: '<div>' + iRecords[i].columnTemplate + '</div>',
                                    components: {
                                        control: controlComponent
                                    },
                                    data() {
                                        return {
                                            data: {}
                                        };
                                    },
                                }, this);
                            this.columnTemplates[iRecords[i].field] = template;
                        }
                        if (iRecords[i].filterTemplate) {
                            let controlType = 'string';
                            switch (iRecords[i].type) {
                                case 1:
                                case 2:
                                case 201:
                                case 16:
                                    controlType = 'number';
                                    break;
                                case 5:
                                case 6:
                                case 9:
                                case 203:
                                    controlType = 'date';
                                    break;
                                case 13:
                                case 205:
                                    controlType = 'dropdown';
                                    break;
                            }
                            let changeFilter = (controlName, search, search1, option, text) => {
                                let filterValue = library.cloneObj(this.filterValue);
                                if (search instanceof Date) {
                                    let day = search.getDate();
                                    if (day < 10) day = '0' + day;
                                    let month = search.getMonth() + 1;
                                    if (month < 10) month = '0' + month;
                                    let year = search.getFullYear();
                                    search = month + '/' + day + '/' + year;
                                }
                                if (controlType === 'dropdown') {
                                    option = text;
                                }
                                search = search || !isNaN(parseInt(search, 10)) ? search + '' : '';
                                search1 = search1 || !isNaN(parseInt(search1, 10)) ? search1 + '' : '';
                                filterValue[controlName] = {
                                    CLIENT_GRID_SEARCH: search,
                                    CLIENT_GRID_SEARCH_1: search1,
                                    CLIENT_GRID_SEARCH_OPTION: parseInt(option, 10) || ""
                                };
                                this.filterValue = filterValue;
                            };
                            let controlItems = [];
                            if (iRecords[i].items) {
                                controlItems = iRecords[i].items;
                            }
                            let value = iRecords[i].CLIENT_GRID_SEARCH ? iRecords[i].CLIENT_GRID_SEARCH : "";
                            if (controlType === 'dropdown') {
                                for (let i in controlItems) {
                                    if (i === 'length') continue;
                                    if (controlItems[i].text === value) value = controlItems[i].value;
                                }
                            }
                            let secondValue = iRecords[i].CLIENT_GRID_SEARCH_1 ? iRecords[i].CLIENT_GRID_SEARCH_1 : "";
                            let ddValue = parseInt(iRecords[i].CLIENT_GRID_SEARCH_OPTION, 10) || 1;
                            this.filterValue[iRecords[i].field] = {
                                CLIENT_GRID_SEARCH: value,
                                CLIENT_GRID_SEARCH_1: secondValue,
                                CLIENT_GRID_SEARCH_OPTION: ddValue
                            };

                            let controlPlaceHolder = iRecords[i].placeholder || '';

                            let templateName = this.cId + "_filter_" + i + "_template_" + library.getUniqueID();
                            let template = library.createTemplateComponent(templateName,
                                {
                                    template: `
                                                    <div @click="click">
                                                        <div v-if="controlType === 'string'">
                                                            <control :name="controlName + '_' + templateName" :prop-config="controlConfig" :value="value" :oninput="onchangeValue" :onchange="onchangeValue" ></control>
                                                        </div>
                                                        <div v-if="controlType === 'number'" class="number-filter">
                                                            <control :name="controlName + '_numberDD_' + templateName" :prop-config="numberDD" :value="ddValue" :onchange="onchangeDD" ></control>
                                                            <control :name="controlName + '_' + templateName" :prop-config="controlConfig" :value="value" :onchange="onchangeValue" ></control>
                                                            <control v-if="ddValue == 7" :name="controlName + '_numberSecond_' + templateName" :value="secondValue" :prop-config="numberSecond" :onchange="onchangeSecond" ></control>
                                                        </div>
                                                        <div v-if="controlType === 'date'" class="date-filter">
                                                            <control :name="controlName + '_dateDD_' + templateName" :prop-config="dateDD" :value="ddValue" :onchange="onchangeDD" ></control>
                                                            <control v-if="ddValue < 100 && datePeriodIsNumber.indexOf(ddValue) === -1" :name="controlName + '_' + templateName" :prop-config="controlConfig" :value="value" :onchange="onchangeValue" ></control>
                                                            <control v-if="datePeriodIsNumber.indexOf(ddValue) !== -1" :name="controlName + '_dateSecond_' + templateName" :prop-config="numberDate" :value="value" :onchange="onchangeValue" ></control>
                                                            <control v-if="ddValue == 9" :name="controlName + '_dateSecond_' + templateName" :prop-config="dateSecond" :value="secondValue" :onchange="onchangeSecond" ></control>
                                                        </div>
                                                        <div v-if="controlType === 'dropdown'">
                                                            <control :name="controlName + '_' + templateName" :prop-config="controlConfig" :onchange="onchangeValue" ></control>
                                                        </div>
                                                    </div>`,
                                    components: {
                                        control: controlComponent
                                    },
                                    data() {
                                        return {
                                            templateName: templateName,
                                            data: {},
                                            controlName: iRecords[i].field,
                                            controlType: controlType,
                                            controlConfig: {
                                                name: iRecords[i].field,
                                                showSpinButton: false,
                                                type: controlType,
                                                value: value,
                                                fields: { text: 'text', value: 'value' },
                                                items: controlItems,
                                                placeholder: controlPlaceHolder
                                            },
                                            dateSecond: {
                                                type: 'date',
                                                value: secondValue
                                            },
                                            datePeriodIsNumber: [81, 82, 90, 91, 92, 93],
                                            dateDD: {
                                                type: 'dropdown',
                                                showClearButton: false,
                                                value: ddValue,
                                                default: 1,
                                                fields: {
                                                    text: 'text',
                                                    value: 'value'
                                                },
                                                items: [
                                                    {
                                                        text: '=',
                                                        value: 1
                                                    },
                                                    {
                                                        text: '>',
                                                        value: 5
                                                    },
                                                    {
                                                        text: '>=',
                                                        value: 6
                                                    },
                                                    {
                                                        text: '<',
                                                        value: 7
                                                    },
                                                    {
                                                        text: '<=',
                                                        value: 8
                                                    },
                                                    {
                                                        text: 'n-n',
                                                        value: 9
                                                    },
                                                    {
                                                        text: 'yesterday',
                                                        value: 101
                                                    },
                                                    {
                                                        text: 'today',
                                                        value: 102
                                                    },
                                                    {
                                                        text: 'today minus',
                                                        value: 90
                                                    },
                                                    {
                                                        text: 'today plus',
                                                        value: 91
                                                    },
                                                    {
                                                        text: 'today through minus',
                                                        value: 92
                                                    },
                                                    {
                                                        text: 'today through plus',
                                                        value: 93
                                                    },
                                                    {
                                                        text: 'today before minus',
                                                        value: 81
                                                    },
                                                    {
                                                        text: 'today after plus',
                                                        value: 82
                                                    },
                                                    {
                                                        text: 'tomorrow',
                                                        value: 103
                                                    },
                                                    {
                                                        text: 'last week',
                                                        value: 111
                                                    },
                                                    {
                                                        text: 'this week',
                                                        value: 112
                                                    },
                                                    {
                                                        text: 'next week',
                                                        value: 113
                                                    },
                                                    {
                                                        text: 'last month',
                                                        value: 121
                                                    },
                                                    {
                                                        text: 'this month',
                                                        value: 122
                                                    },
                                                    {
                                                        text: 'next month',
                                                        value: 123
                                                    },
                                                    {
                                                        text: 'last quarter',
                                                        value: 131
                                                    },
                                                    {
                                                        text: 'this quarter',
                                                        value: 132
                                                    },
                                                    {
                                                        text: 'next quarter',
                                                        value: 133
                                                    },
                                                    {
                                                        text: 'last year',
                                                        value: 141
                                                    },
                                                    {
                                                        text: 'this year',
                                                        value: 142
                                                    },
                                                    {
                                                        text: 'next year',
                                                        value: 143
                                                    }
                                                ]
                                            },
                                            ddValue: ddValue,
                                            secondValue: secondValue,
                                            value: value,
                                            numberDD: {
                                                type: 'dropdown',
                                                value: ddValue,
                                                showClearButton: false,
                                                default: 1,
                                                fields: {
                                                    text: 'text',
                                                    value: 'value'
                                                },
                                                items: [
                                                    {
                                                        text: '=',
                                                        value: 1
                                                    },
                                                    {
                                                        text: '>',
                                                        value: 3
                                                    },
                                                    {
                                                        text: '>=',
                                                        value: 4
                                                    },
                                                    {
                                                        text: '<',
                                                        value: 5
                                                    },
                                                    {
                                                        text: '<=',
                                                        value: 6
                                                    },
                                                    {
                                                        text: 'n-n',
                                                        value: 7
                                                    },
                                                ]
                                            },
                                            numberSecond: {
                                                type: 'number',
                                                showSpinButton: false,
                                                value: secondValue
                                            },
                                            numberDate: {
                                                type: 'number',
                                                showSpinButton: false,
                                                value: secondValue,
                                                min: 1
                                            }
                                        };
                                    },
                                    methods: {
                                        click(event) {
                                            event.stopPropagation();
                                        },
                                        onchange() {
                                            let value = this.value;
                                            let id = false;
                                            if (this.controlType === 'dropdown') {
                                                for (let i in this.controlConfig.items) {
                                                    if (i === 'length') continue;
                                                    if (this.controlConfig.items[i].value === this.value) {
                                                        value = this.controlConfig.items[i].text;
                                                        id = this.controlConfig.items[i].value;
                                                    }
                                                }
                                            }
                                            changeFilter(this.controlName, value, this.secondValue, this.ddValue, id);
                                        },
                                        onchangeValue(event) {
                                            if (typeof event.value !== 'undefined') {
                                                this.value = event.value;
                                                this.onchange();
                                            }
                                        },
                                        onchangeSecond(event) {
                                            if (typeof event.value !== 'undefined') {
                                                this.secondValue = event.value;
                                                this.onchange();
                                            }
                                        },
                                        onchangeDD(event) {
                                            if (typeof event.value !== 'undefined') {
                                                this.ddValue = event.value;
                                                this.onchange();
                                            }
                                        }
                                    }
                                }, this);
                            tmpFilterTemplates[iRecords[i].field] = template;
                        }
                        iHeaders.push(iRecords[i]);
                    }
                } else {
                    for (let i in iRecords[0]) {
                        if (i === 'length') continue;
                        if (i === 'rowID') continue;
                        iHeaders.push({
                            field: i,
                            text: i,
                            headerText: i
                        });
                    }
                }
                this.filterTemplates = library.cloneObj(tmpFilterTemplates);
                this.width = this.isForProfessions ? '245' : width;
                return iHeaders;
            };
            if (this.config && this.config.tableHeaders) {
                tmpHeaders = [];
                for (let i in this.config.tableHeaders) {
                    if (i === 'length') continue;
                    let tmp = library.cloneObj(this.config.tableHeaders[i]);
                    tmp.headerText = tmp.text;
                    tmpHeaders.push(tmp);
                    if (tmp.columnTemplate) {
                        if (!this.columnTemplates) this.columnTemplates = {};
                        let componentsObj = {};
                        if (tmp.templateComponents) {
                            for (let cmpIndex in tmp.templateComponents) {
                                if (cmpIndex === 'length') continue;
                                let iComponent = tmp.templateComponents[cmpIndex];
                                let filename;
                                let directory;
                                let project;
                                let path = iComponent.componentPath.split('/');
                                filename = path.pop();
                                if (path.length === 2) directory = path.pop(); // NOSONAR
                                project = path.pop();
                                if (project === '@common') {
                                    if (directory) componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@common/${directory}/${filename}`));
                                    else componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@common/${filename}`));
                                } else if (project === '@components') {
                                    if (directory) componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@components/${directory}/${filename}`));
                                    else componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@components/${filename}`));
                                } else if (project === '@signin') {
                                    if (directory) componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@signin/${directory}/${filename}`));
                                    else componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@signin/${filename}`));
                                } else if (project === '@ecDashboard') {
                                    if (directory) componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@ecDashboard/${directory}/${filename}`));
                                    else componentsObj[iComponent.htmlTag] = defineAsyncComponent(() => import(`@ecDashboard/${filename}`));
                                }
                            }
                        }
                        let template = library.createTemplateComponent(this.cId + "_column_" + i + "_template",
                            {
                                template: '<div>' + tmp.columnTemplate + '</div>',
                                components: {
                                    control: controlComponent,
                                    ...componentsObj
                                },
                                data() {
                                    return {
                                        data: {}
                                    };
                                },
                            }, this);
                        this.columnTemplates[tmp.field] = template;
                    }
                }
            }
            if ((this.data && this.data.records) || (this.isMainGrid)) {
                tmpRecords = [];
                if (typeof this.data?.records !== 'undefined' && Array.isArray(this.data.records)) {
                    this.data.records.forEach((tmpRecord) => {
                        if (!tmpRecord.CNT_COL) tmpRecords.push(tmpRecord);
                    });
                } else {
                    tmpRecords = library.cloneObj(this.data.records);
                }

                if (!tmpHeaders) {
                    if (typeof tmpHeaders === 'undefined') tmpHeaders = [];
                    if (this.isMainGrid || typeof this.data.headers !== 'undefined') tmpHeaders = tmpHeaders.concat(getHeadersFromSampleRecord(this.data.headers, true));
                    else tmpHeaders = tmpHeaders.concat(getHeadersFromSampleRecord(tmpRecords));
                }
            }
            if (this.data && this.data.summary) {
                tmpSummary = [];
                for (let i in this.data.summary) {
                    if (i === 'length') continue;
                    let tmp = {};
                    tmp.data = library.cloneObj(this.data.summary[i]);
                    tmp.type = 'custom';
                    tmp.field = i;
                    tmpSummary.push(tmp);
                }
            }
            let grouping = false;
            let groupSettingsColumns = [];
            let sortSettingsColumns = [];
            this.sortingColumns = [];
            for (let i in tmpHeaders) {
                if (i === 'length') continue;
                if (tmpHeaders[i].isSortable) {
                    if (tmpHeaders[i].CLIENT_GRID_SORT_GROUP_BY) {
                        grouping = true;
                        groupSettingsColumns[tmpHeaders[i].CLIENT_GRID_SORT_POS - 1] = tmpHeaders[i].field;
                        this.groupingColumns[tmpHeaders[i].field] = 1;
                    }
                    sortSettingsColumns[tmpHeaders[i].CLIENT_GRID_SORT_POS - 1] = {
                        direction: tmpHeaders[i].CLIENT_GRID_SORT_DIR ? 'Descending' : 'Ascending', // @todo recheck
                        field: tmpHeaders[i].field
                    };
                    if (tmpHeaders[i].CLIENT_GRID_SORT_POS) {
                        this.sortingColumns[tmpHeaders[i].CLIENT_GRID_SORT_POS - 1] = tmpHeaders[i].field;
                    }
                }
            }
            groupSettingsColumns = groupSettingsColumns.filter((tmpEl) => typeof tmpEl !== 'undefined');
            let captionTemplate = library.createTemplateComponent(this.cId + "_caption__template",
                {
                    // template: `<div>{{data.headerText}}: {{data.key || 'n/a'}} - {{data.count}} items</div>`,
                    template: `<div></div>`,
                    components: {
                        control: controlComponent
                    },
                    data() {
                        return {
                            data: {},
                        };
                    },
                }, this);
            if (grouping) {
                this.gridSettings.grouping = true;
                this.gridSettings.groupSettings = {
                    showGroupedColumn: true,
                    showDropArea: true,
                    columns: groupSettingsColumns,
                    captionTemplate: captionTemplate
                };
            } else {
                this.gridSettings.groupSettings = {
                    showGroupedColumn: true,
                    showDropArea: true,
                    columns: [],
                    captionTemplate: captionTemplate
                };
            }
            if (sortSettingsColumns.length) {
                if (sortSettingsColumns.length === 1) {
                    sortSettingsColumns.push({
                        direction: "Ascending",
                        field: "SEARCH"
                    });
                }
                this.gridSettings.sortSettings = { allowUnsort: false, columns: sortSettingsColumns };
            }
            if (!tmpHeaders || library.objIsEmpty(tmpHeaders)) {
                tmpHeaders = [{
                    field: 'NO_RECORDS_TO_DISPLAY',
                    headerText: '',
                    text: ''
                }];
            }
            this.headers = library.cloneObj(tmpHeaders, true, true);
            let tmpAggregates = [];
            if (this.gridSettings.grouping) {
                for (let i in tmpHeaders) {
                    if (tmpHeaders[i].field !== 'SEARCH') {
                        let tmpTemplateName = this.cId + "_group_caption__template_" + tmpHeaders[i].field + library.getUniqueID();
                        let template = library.createTemplateComponent(tmpTemplateName,
                            {
                                template: `<div v-html="data.Custom"></div>`,
                                components: {
                                    control: controlComponent
                                },
                                data() {
                                    return {
                                        data: {}
                                    };
                                },
                            }, this);
                        tmpAggregates.push({
                            field: tmpHeaders[i].field,
                            type: 'custom',
                            customAggregateFn: (data) => {
                                let fieldValues = data.items.map((tmp) => tmp[tmpHeaders[i].field]);
                                return fieldValues.every((tmp) => tmp === fieldValues[0]) ? fieldValues[0] : '';
                            },
                            groupCaptionTemplate: template,
                        });
                    }
                }
                // eslint-disable-next-line no-template-curly-in-string
                this.gridSettings.groupSettings.captionTemplate = '<div class="group-checkbox"></div>';
            }

            this.aggregates = library.cloneObj(tmpAggregates, true, true);
            this.summary = tmpSummary;
            for (let i in tmpRecords) {
                for (let j in tmpRecords[i]) {
                    if (typeof tmpRecords[i][j] === 'object' && !library.objIsEmpty(tmpRecords[i][j])) {
                        try {
                            tmpRecords[i][j] = Object.values(tmpRecords[i][j]).join(',\n');
                        } catch (e) {
                            // do nothing
                        }
                    }
                }
            }
            this.records = tmpRecords;
            this.$nextTick(() => {
                this.preparingData = false;
                this.$emit('update:preparingData', false);
            });
        },
        refresh() {
            if (this.$refs && this.$refs[this.ref] && this.$refs[this.ref].ej2Instances) {
                this.$refs[this.ref].ej2Instances.refresh();
            }
        },
        rowSelected() {
            let tmpSelectedRecords = [];
            let tmpSelectedItemsFullData = [];
            if (this.$refs && this.$refs[this.ref] && typeof this.$refs[this.ref].getSelectedRecords === 'function') {
                let selectedRecords = this.$refs[this.ref].getSelectedRecords();
                tmpSelectedItemsFullData = library.cloneObj(selectedRecords);
                for (let i in selectedRecords) {
                    if (i === 'length') continue;
                    tmpSelectedRecords.push(selectedRecords[i].rowID);
                }
                let selectedRowIndexes = this.$refs[this.ref].ej2Instances.getSelectedRowIndexes();
                let checker = (arr, target) => target.every((v) => arr.includes(v));
                let groupSelected = {};
                for (let i in this.recordsByGroups) {
                    if (checker(selectedRowIndexes, this.recordsByGroups[i])) {
                        groupSelected[i] = true;
                    } else {
                        groupSelected[i] = false;
                    }
                }
                this.groupSelected = library.cloneObj(groupSelected);
            }
            this.selectedItems = tmpSelectedRecords;
            this.selectedItemsFullData = tmpSelectedItemsFullData;
            this.$emit('update:propSelectedItems', tmpSelectedRecords);
            this.$emit('update:propSelectedItemsFullData', tmpSelectedItemsFullData);
        },
        contextMenuOpen(args) {
            if (this.isMainGrid && args.rowInfo && args.rowInfo.target && args.rowInfo.target.classList && (args.rowInfo.target.classList.contains('e-headercelldiv') || args.rowInfo.target.classList.contains('e-content'))) {
                args.cancel = true;
            }
            if (typeof this.ctxMenuOpenFnc === 'function') {
                let tmpSelectedItemsFullData;
                if (typeof args?.rowInfo?.rowData !== 'undefined') {
                    tmpSelectedItemsFullData = args.rowInfo.rowData;
                }
                this.ctxMenuOpenFnc(args, tmpSelectedItemsFullData, this.$refs[this.ref]);
            }
        },
        contextMenuClick(args) {
            if (args && args.rowInfo && args.rowInfo.rowData && args.rowInfo.rowData.rowID) {
                let act = args.item.id.toLowerCase();
                let isCustom = args.item.isCustom || false;
                let recordId = args.rowInfo.rowData.rowID;
                if (this.isMainGrid && !isCustom) {
                    let params = {
                        recordId: recordId
                    };
                    if (act === 'clipboard') params.text = args.rowInfo.target.innerText;
                    if (act === 'newlogbook') act = 'new_logbook_lbe';
                    if (act === 'quicklogbook') act = 'addhours';
                    this.doAction(act, params);
                } else if (typeof this.ctxClickFnc === 'function') {
                    let tmpSelectedItemsFullData;
                    if (typeof args?.rowInfo?.rowData !== 'undefined') {
                        tmpSelectedItemsFullData = args.rowInfo.rowData;
                    }
                    this.ctxClickFnc(act, tmpSelectedItemsFullData);
                }
            }
        },
        recordDoubleClick(args) {
            if (args && args.rowData && args.rowData.rowID) {
                let recordId = args.rowData.rowID;
                if (this.isMainGrid && this.psp !== 32) {
                    this.doAction('edit', { recordId: recordId });
                }
                if (typeof this.dblClickFnc === 'function') {
                    this.dblClickFnc(recordId, args.rowData);
                }
            }
        },
        async gridSearch(clear, clearSorting) {
            let gridInfo = library.cloneObj(this.gridInfo);
            delete gridInfo.CLIENT_GRID_SAVED_ID;
            delete gridInfo.CLIENT_GRID_SAVED_NAME;
            if (!gridInfo.columns || !gridInfo.columns.length) {
                library.hideLoadingIndicator(this.cId + '_wrapper');
                return;
            }
            let newColumns = library.cloneObj(gridInfo.columns);
            let currentSortPos = 1;
            for (let i in newColumns) {
                if (clearSorting) {
                    if (newColumns[i].GRID_FIELD_NAME !== clearSorting) {
                        newColumns[i].CLIENT_GRID_SORT_POS = 0;
                    } else {
                        newColumns[i].CLIENT_GRID_SORT_POS = 1;
                    }
                } else if (newColumns[i].CLIENT_GRID_SORT_POS) currentSortPos = newColumns[i].CLIENT_GRID_SORT_POS;
            }
            let changed = false;
            let filter = library.cloneObj(this.filterValue);
            if (clear) {
                gridInfo.FILTER_ID = -1;
                gridInfo.CLIENT_GRID_AFF_TYPE = -1;
                gridInfo.DATA_VIEW_ID = 3;
            }
            this.$nextTick(async () => {
                let tmpColumns = this.$refs[this.ref].getColumns();
                for (let i in newColumns) {
                    if (i === 'length') continue;
                    let fieldName = newColumns[i].GRID_FIELD_NAME;
                    let columnFormGrid = tmpColumns.find((tmpColumn) => tmpColumn.field === fieldName);
                    if (columnFormGrid) {
                        let direction = columnFormGrid.sortDirection === 'Ascending' ? 0 : 1;
                        if (newColumns[i].CLIENT_GRID_SORT_DIR !== direction) {
                            newColumns[i].CLIENT_GRID_SORT_DIR = direction;
                            changed = true;
                        }
                        if (this.groupingColumns[fieldName]) {
                            if (!newColumns[i].CLIENT_GRID_SORT_GROUP_BY) changed = true;
                            newColumns[i].CLIENT_GRID_SORT_GROUP_BY = 1;
                            if (!newColumns[i].CLIENT_GRID_SORT_POS) newColumns[i].CLIENT_GRID_SORT_POS = ++currentSortPos;
                        } else {
                            if (newColumns[i].CLIENT_GRID_SORT_GROUP_BY) changed = true;
                            newColumns[i].CLIENT_GRID_SORT_GROUP_BY = 0;
                        }
                    }
                    if (filter[fieldName]) {
                        if (clear) {
                            if (this.controls && this.controls[fieldName] && this.controls[fieldName].type !== 'dropdown') this.controls[fieldName].value = '';
                            if (this.controls && this.controls[fieldName] && this.controls[fieldName].type === 'dropdown') this.controls[fieldName].value = null;
                            if (this.controls && this.controls[fieldName + '_numberDD']) this.controls[fieldName].value = null;
                            if (this.controls && this.controls[fieldName + '_numberSecond']) this.controls[fieldName].value = '';
                            if (this.controls && this.controls[fieldName + '_dateDD']) this.controls[fieldName].value = null;
                            if (this.controls && this.controls[fieldName + '_dateSecond']) this.controls[fieldName].value = '';
                            if (this.controls && this.controls[fieldName]) this.controls[fieldName].value = '';
                            filter[fieldName].CLIENT_GRID_SEARCH = "";
                            filter[fieldName].CLIENT_GRID_SEARCH_1 = "";
                            filter[fieldName].CLIENT_GRID_SEARCH_OPTION = "";
                        }
                        if (newColumns[i].CLIENT_GRID_SEARCH !== filter[fieldName].CLIENT_GRID_SEARCH) {
                            newColumns[i].CLIENT_GRID_SEARCH = filter[fieldName].CLIENT_GRID_SEARCH;
                            if (!newColumns[i].CLIENT_GRID_SORT_POS) newColumns[i].CLIENT_GRID_SORT_POS = 0;
                            changed = true;
                        }
                        if (newColumns[i].CLIENT_GRID_SEARCH_1 !== filter[fieldName].CLIENT_GRID_SEARCH_1) {
                            newColumns[i].CLIENT_GRID_SEARCH_1 = filter[fieldName].CLIENT_GRID_SEARCH_1;
                            if (!newColumns[i].CLIENT_GRID_SORT_POS) newColumns[i].CLIENT_GRID_SORT_POS = 0;
                            changed = true;
                        }
                        let checkSearchValue = true;
                        let isDate = [5, 6, 9, 203].indexOf(newColumns[i].TYPE) !== -1;
                        if (isDate && parseInt(filter[fieldName].CLIENT_GRID_SEARCH_OPTION, 10) > 100) {
                            checkSearchValue = false;
                        }
                        if (!newColumns[i].CLIENT_GRID_SEARCH && !newColumns[i].CLIENT_GRID_SEARCH_1 && checkSearchValue) {
                            newColumns[i].CLIENT_GRID_SEARCH_OPTION = 0;
                            filter[fieldName].CLIENT_GRID_SEARCH_OPTION = 0;
                        }
                        if (parseInt(newColumns[i].CLIENT_GRID_SEARCH_OPTION, 10) !== parseInt(filter[fieldName].CLIENT_GRID_SEARCH_OPTION, 10)) {
                            newColumns[i].CLIENT_GRID_SEARCH_OPTION = parseInt(filter[fieldName].CLIENT_GRID_SEARCH_OPTION, 10);
                            if (!newColumns[i].CLIENT_GRID_SORT_POS) newColumns[i].CLIENT_GRID_SORT_POS = 0;
                            changed = true;
                        }
                    }
                }
                if (changed) {
                    gridInfo.columns = newColumns;
                    if (!this.isList) {
                        library.showLoadingIndicator(this.cId + '_wrapper');
                        await configStore.setGridSettings(this.psp, gridInfo);
                    }
                    this.reloadGrid();
                }
            });
        },
        async processAction(act) {
            switch (act) {
                case 'trapEnter':
                    if (this.selectedGrid) await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': null });
                    this.gridSearch();
                    break;
            }
        },
        async groupingChange(event) {
            this.reloadingData = true;
            this.prevSelectedItems = library.cloneObj(this.selectedItems);
            if (this.isMainGrid) {
                let gridInfo = library.cloneObj(this.gridInfo);
                if (typeof this.gridSettings === 'undefined') this.gridSettings = {};
                if (typeof this.gridSettings.groupSettings === 'undefined') this.gridSettings.groupSettings = {};
                if (typeof this.gridSettings.columns === 'undefined') this.gridSettings.columns = [];
                let tmpGroupingColumns = library.cloneObj(this.gridSettings.groupSettings.columns);
                switch (event.requestType) {
                    case 'grouping':
                        tmpGroupingColumns.push(event.columnName);
                        break;
                    case 'ungrouping':
                        tmpGroupingColumns = library.pullAll(tmpGroupingColumns, [event.columnName]);
                        break;
                }
                this.groupingColumns = {};
                for (let i in tmpGroupingColumns) {
                    this.groupingColumns[tmpGroupingColumns[i]] = 1;
                }
                this.gridSettings.groupSettings.columns = tmpGroupingColumns;
                for (let i in gridInfo.columns) {
                    let gfName = gridInfo.columns[i].GRID_FIELD_NAME;
                    if (this.groupingColumns[gfName]) {
                        gridInfo.columns[i].CLIENT_GRID_SORT_GROUP_BY = 1;
                        gridInfo.columns[i].CLIENT_GRID_SORT_POS = tmpGroupingColumns.indexOf(gfName) + 1;
                    } else {
                        gridInfo.columns[i].CLIENT_GRID_SORT_GROUP_BY = 0;
                    }
                }
                library.showLoadingIndicator(this.cId + '_wrapper');
                await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': null });
                delete gridInfo.CLIENT_GRID_SAVED_ID;
                delete gridInfo.CLIENT_GRID_SAVED_NAME;
                await configStore.setGridSettings(this.psp, gridInfo);
                this.reloadGrid();
            }
        },
        actionBegin(event) {
            if (this.isMainGrid) {
                switch (event.requestType) {
                    case 'sorting':
                        if (event?.target?.classList?.contains('e-sortnumber')) {
                            event.cancel = true;
                            this.doAction('customizeSort', {});
                        } else {
                            let clearSorting = false;
                            if (this.sortingColumns.indexOf(event?.columnName) === -1) clearSorting = event?.columnName;
                            this.gridSearch(false, clearSorting);
                        }
                        break;
                    case 'grouping':
                    case 'ungrouping':
                        this.groupingChange(event);
                        break;
                }
            }
            if (event.requestType === "columnstate" && typeof this.columnsVisibilityChangeFnc === 'function') {
                this.columnsVisibilityChangeFnc(event);
            }
        },
        resizeStart() {
            this.width = 'auto';
        },
        async resizeStop(event) {
            if (event && event.column && event.column.field && event.column.field !== 'SEARCH' && event.column.width) {
                let width = (event.column.width + '').replace('px', '');
                let columnName = event.column.field;
                if (this.isMainGrid) {
                    let gridInfo = library.cloneObj(this.gridInfo);
                    let changed = false;
                    for (let i in gridInfo.columns) {
                        if (i === 'length') continue;

                        if (gridInfo.columns[i] && gridInfo.columns[i].GRID_FIELD_NAME === columnName) {
                            if (Math.abs(parseInt(gridInfo.columns[i].CLIENT_GRID_WIDTH, 10) - parseInt(width, 10)) > 10) {
                                gridInfo.columns[i].CLIENT_GRID_WIDTH = parseInt(width, 10);
                                changed = true;
                            } else {
                                // double click handling
                                // eslint-disable-next-line no-await-in-loop
                                await library.sleep(100);
                                width = document.querySelector("th[aria-describedby='headerTitle-" + event.column.uid + "']")?.clientWidth;

                                if (width && Math.abs(parseInt(gridInfo.columns[i].CLIENT_GRID_WIDTH, 10) - parseInt(width, 10)) > 10) {
                                    gridInfo.columns[i].CLIENT_GRID_WIDTH = parseInt(width, 10);
                                    changed = true;
                                }
                            }
                        }
                    }

                    if (this.gridInfo.SUMMARY_MODE) {
                        for (let i in this.headers) {
                            if (i === 'length') continue;

                            if (this.headers[i] && this.headers[i].field === columnName) {
                                this.headers[i].width = width;
                                break;
                            }
                        }

                        if (document.getElementById('summary_' + columnName)) {
                            document.getElementById('summary_' + columnName).style.width = width + 'px';
                        }

                        if (document.getElementById('avarage_' + columnName)) {
                            document.getElementById('avarage_' + columnName).style.width = width + 'px';
                        }
                    }

                    if (changed) {
                        this.gridSettings.resizing = false;
                        library.showLoadingIndicator(this.cId + '_wrapper');
                        await configStore.setGridSettings(this.psp, gridInfo);
                        this.gridSettings.resizing = true;
                        await configStore.getGridSettings(this.psp, this.selectedGrid, true);
                        library.hideLoadingIndicator(this.cId + '_wrapper');
                    }
                }
                if (typeof this.resizeStopFnc === 'function') this.resizeStopFnc(columnName, width);
            }
        },
        mouseUpListener(e) {
            document.removeEventListener('mouseup', this.mouseUpListener, true);
            setTimeout(async () => {
                if (this.columnRemoved) {
                    let gridInfo = library.cloneObj(this.gridInfo);
                    let tmpColumn = gridInfo.columns.find((elem) => elem.GRID_FIELD_NAME === this.columnRemoved);
                    if (tmpColumn) {
                        if (tmpColumn.MANDATORY) {
                            let title = await ddStore.getAlias('COMMON', 'WARNINGMESSAGE_TITLE');
                            let message = await ddStore.getAlias('DATA_GRID', 'GRIDENGINE_ERR_DETACH_MANDATORY');
                            library.showDialog(message, false, false, { title: title, showAsAlert: true });
                        } else {
                            if (this.$refs && this.$refs[this.ref]) {
                                let tmpColumns = this.$refs[this.ref].getColumns();
                                let order = [];
                                for (let i in tmpColumns) {
                                    if (i === 'length') continue;
                                    if (tmpColumns[i].field && tmpColumns[i].field !== this.columnRemoved) {
                                        order.push(tmpColumns[i].field);
                                    }
                                }
                                if (this.isMainGrid) {
                                    if (!order.length) return;
                                    let gridInfo = library.cloneObj(this.gridInfo);
                                    if (!gridInfo.columns || !gridInfo.columns.length) return;
                                    let newColumns = [];
                                    for (let i in order) {
                                        if (i === 'length') continue;
                                        let tmpColumn = gridInfo.columns.find((elem) => elem.GRID_FIELD_NAME === order[i]);
                                        if (tmpColumn) {
                                            newColumns.push(tmpColumn);
                                        }
                                    }
                                    if (newColumns.length) {
                                        await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': null });
                                        delete gridInfo.CLIENT_GRID_SAVED_ID;
                                        delete gridInfo.CLIENT_GRID_SAVED_NAME;
                                        gridInfo.columns = newColumns;
                                        library.showLoadingIndicator(this.cId + '_wrapper');
                                        await configStore.setGridSettings(this.psp, gridInfo);
                                        this.reloadGrid();
                                    }
                                }
                            }
                        }
                    }
                }
            }, 500);
        },
        columnDragStart(args) {
            this.columnRemoved = args.column.field;
            document.removeEventListener('mouseup', this.mouseUpListener, true);
            document.addEventListener('mouseup', this.mouseUpListener, true);
        },
        columnDrop() {
            this.columnRemoved = false;
            if (this.$refs && this.$refs[this.ref]) {
                this.$nextTick(async () => {
                    let tmpColumns = this.$refs[this.ref].getColumns();
                    let order = [];
                    for (let i in tmpColumns) {
                        if (i === 'length') continue;
                        if (tmpColumns[i].field) {
                            order.push(tmpColumns[i].field);
                        }
                    }
                    if (this.isMainGrid) {
                        if (!order.length) return;
                        let gridInfo = library.cloneObj(this.gridInfo);
                        if (!gridInfo.columns || !gridInfo.columns.length) return;
                        let newColumns = [];
                        for (let i in order) {
                            if (i === 'length') continue;
                            let tmpColumn = gridInfo.columns.find((elem) => elem.GRID_FIELD_NAME === order[i]);
                            if (tmpColumn) {
                                newColumns.push(tmpColumn);
                            }
                        }
                        if (newColumns.length && !library.isEqualObj(gridInfo.columns, newColumns)) {
                            library.showLoadingIndicator(this.cId + '_wrapper');
                            await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': null });
                            delete gridInfo.CLIENT_GRID_SAVED_ID;
                            delete gridInfo.CLIENT_GRID_SAVED_NAME;
                            gridInfo.columns = newColumns;
                            await configStore.setGridSettings(this.psp, gridInfo);
                            this.reloadGrid();
                        }
                    } else if (typeof this.columnDropFnc === 'function') this.columnDropFnc(order);
                });
            }
        },
        async getMainGridData() {
            if (!this.psp) return;
            library.showLoadingIndicator(this.cId + '_wrapper');
            this.selectedGrid = null;
            this.selectedFilter = null;
            this.selectedGridType = -1;
            let mainGridData = await this.sDs.post('ec_getMainGridInfo', this.psp, ['gridControls', 'grids', 'filters', 'menuContext']);
            if (mainGridData.data) {
                mainGridData = mainGridData.data;
            } else {
                return;
            }
            if (mainGridData.menuContext) {
                this.prepareCtxMenu(mainGridData.menuContext);
            }
            this.prepareGridControls(mainGridData.gridControls);
            this.prepareGrids(mainGridData.grids);
            this.prepareFilters(mainGridData.filters);
            this.getGridData(true);
        },
        prepareCtxMenu(buttons) {
            let tmpButtons = [];
            for (let i in buttons) {
                if (i === 'length') continue;
                tmpButtons.push({
                    text: buttons[i].title,
                    id: buttons[i].action
                });
            }
            for (let i in this.customCtxMenu) {
                if (i === 'length') continue;
                let isCustom = true;
                if (['clipboard'].indexOf(this.customCtxMenu[i].action) !== -1) isCustom = false;
                tmpButtons.push({
                    text: this.customCtxMenu[i].title,
                    id: this.customCtxMenu[i].action,
                    isCustom: isCustom
                });
            }
            this.$nextTick(() => {
                this.ctxMenu = tmpButtons;
            });
        },
        prepareGridControls(controls) {
            let tmpGridControls = {};
            for (let i in controls) {
                if (i === 'length') continue;
                tmpGridControls[controls[i].name] = controls[i].title || ' ';
            }
            if (!library.isEqualObj(this.gridControls, tmpGridControls)) {
                this.gridControls = tmpGridControls;
            }
        },
        prepareFilters(filters) {
            let tmpFilters = [];
            let selectedFilter = null;
            for (let i in filters) {
                if (i === 'length') continue;
                if (filters[i].selected) {
                    selectedFilter = filters[i].FILTER_ID;
                }
                tmpFilters.push({
                    text: filters[i].FILTER_TITLE,
                    value: filters[i].FILTER_ID
                });
            }
            if (selectedFilter && selectedFilter !== this.selectedFilter) {
                this.selectedFilter = selectedFilter;
            }
            if (!library.isEqualObj(this.filters, tmpFilters)) {
                this.filters = tmpFilters;
            }
        },
        prepareGrids(grids) {
            let tmpGrids = [];
            let selectedGrid = null;
            for (let i in grids) {
                if (i === 'length') continue;
                if (grids[i].selected) {
                    selectedGrid = grids[i].CLIENT_GRID_SAVED_ID;
                }
                tmpGrids.push({
                    text: grids[i].CLIENT_GRID_SAVED_NAME,
                    value: grids[i].CLIENT_GRID_SAVED_ID
                });
            }
            if (selectedGrid && selectedGrid !== this.selectedGrid) {
                this.selectedGrid = selectedGrid;
            }
            if (!library.isEqualObj(this.grids, tmpGrids)) {
                this.grids = tmpGrids;
            }
        },
        selectPrevious() {
            this.$nextTick(() => {
                let prevSelectedIndexes = [];
                let gridRecords = this.$refs[this.ref].ej2Instances.getRowsObject();
                if (this.prevSelectedItems.length && gridRecords && gridRecords.length) {
                    gridRecords.forEach((record, i) => {
                        if (this.prevSelectedItems.indexOf(record.data.ID) !== -1) {
                            prevSelectedIndexes.push(record.index);
                        }
                    });
                }
                if (prevSelectedIndexes.length && typeof this.$refs?.[this.ref]?.ej2Instances?.selectRows === 'function') {
                    this.$refs[this.ref].ej2Instances.selectRows(prevSelectedIndexes);
                }
            });
        },
        async getGridData(refresh) {
            this.reloadingData = true;
            this.prevSelectedItems = library.cloneObj(this.selectedItems);
            if (refresh) {
                this.records = [];
                this.currentPage = 1;
                this.$emit('update:propSelectedItems', []);
            }
            let rowCount = 100; // NOSONAR
            library.showLoadingIndicator(this.cId + '_wrapper');
            await configStore.getGridSettings(this.psp, this.selectedGrid, refresh);
            let gridInfo = library.cloneObj(this.gridInfo);
            if (gridInfo.DATA_VIEW_ID) {
                if (configStore.state.cfg.folders[this.psp]) {
                    let tmpFolder = configStore.state.cfg.folders[this.psp].find((tmp) => tmp.DATA_VIEW_ID === gridInfo.DATA_VIEW_ID);
                    if (tmpFolder) {
                        this.selectedFolder = tmpFolder;
                        this.$emit('update:propSelectedFolder', tmpFolder);
                    }
                }
            }
            if (gridInfo.FILTER_ID && parseInt(gridInfo.FILTER_ID, 10) > 0 && this.selectedFilter !== gridInfo.FILTER_ID) {
                this.selectedFilter = gridInfo.FILTER_ID;
            }
            this.selectedGridType = parseInt(gridInfo.CLIENT_GRID_AFF_TYPE, 10);
            if (gridInfo.CLIENT_GRID_SIZE) {
                rowCount = gridInfo.CLIENT_GRID_SIZE;
                this.pageSize = gridInfo.CLIENT_GRID_SIZE;
            }
            let currentOffset = (this.currentPage - 1) * rowCount;
            let params = {
                'offset': currentOffset,
                'rowCount': rowCount,
                'returnRowCount': true,
                'forceHtml': true,
                'includeObjIDs': true
            };
            let tmpColumns = [];
            this.data = {};
            if (gridInfo && gridInfo.columns) {
                let tmpGridHeaders = [];
                if (this.isForProfessions && this.psp === 2) {
                    tmpGridHeaders.push({
                        "GRID_FIELD_NAME": "Opportunities",
                        "CLIENT_GRID_WIDTH": '200px',
                        "CLIENT_GRID_SORT_POS": 1,
                        "CLIENT_GRID_SORT_DIR": 0,
                        "CLIENT_GRID_SEARCH": "",
                        "CLIENT_GRID_SEARCH_1": "",
                        "CLIENT_GRID_SEARCH_OPTION": "",
                        "isSortable": 1,
                        "isPossibleSort": 1,
                        "drawTitle": "Opportunities",
                        "MANDATORY": 1,
                        "TYPE": 3,
                        "MULTIPLE": 0,
                        "DEFAULT_WIDTH": '200px',
                        "field": "title",
                        "width": '200px',
                        "headerText": 'Opportunities',
                        "text": 'Opportunities',
                        "columnTemplate": '<div v-html="data[\'title\']"></div>',
                        "filterTemplate": true
                    });
                } else {
                    for (let i in gridInfo.columns) {
                        if (i === 'length') continue;
                        let width = gridInfo.columns[i].CLIENT_GRID_WIDTH ? gridInfo.columns[i].CLIENT_GRID_WIDTH : gridInfo.columns[i].DEFAULT_WIDTH;
                        let gridHeader = library.cloneObj(gridInfo.columns[i]);
                        gridHeader.field = gridInfo.columns[i].GRID_FIELD_NAME;
                        gridHeader.text = gridInfo.columns[i].drawTitle;
                        gridHeader.width = width;
                        gridHeader.headerText = gridInfo.columns[i].drawTitle;
                        gridHeader.type = gridInfo.columns[i].TYPE;
                        gridHeader.placeholder = gridInfo.columns[i].searchPlaceholder || '';
                        gridHeader.CLIENT_GRID_SEARCH_OPTION = gridInfo.columns[i].CLIENT_GRID_SEARCH_OPTION + '';
                        if (gridInfo.columns[i].items) {
                            let tmpItems = [];
                            for (let j in gridInfo.columns[i].items) {
                                if (j === 'length') continue;
                                tmpItems.push({
                                    text: gridInfo.columns[i].items[j].title,
                                    value: gridInfo.columns[i].items[j].id + '',
                                });
                            }
                            gridHeader.items = tmpItems;
                        }
                        if (gridInfo.columns[i].TYPE === 13 || gridInfo.columns[i].TYPE === 214 || this.isForProfessions) {
                            gridHeader['columnTemplate'] = '<div v-html="data[\'' + gridInfo.columns[i].GRID_FIELD_NAME + '\']"></div>';
                        }
                        gridHeader['filterTemplate'] = true;
                        tmpColumns.push(gridInfo.columns[i].GRID_FIELD_NAME);
                        tmpGridHeaders.push(gridHeader);
                    }
                }
                this.data.headers = tmpGridHeaders;
            }
            let result;
            if (this.isForProfessions) {
                let searchValue = '';
                if (this.filterValue && this.filterValue['title'] && this.filterValue['title'].CLIENT_GRID_SEARCH) {
                    searchValue = this.filterValue['title'].CLIENT_GRID_SEARCH;
                }
                let selectedFolder = this.selectedFolder || 3;
                result = await this.sDs.post('ec_getList', this.psp, searchValue, selectedFolder, null);
            } else {
                result = await this.sDs.post('ec_getCurrentGrid', this.psp, params);
            }
            if (result && result.status === 'success') {
                if (!result.data.records) {
                    result.data.records = [];
                }
                if (!result.data.summary) {
                    result.data.summary = [];
                }
                if (typeof result.data.rowCount !== 'undefined') {
                    this.totalRecordsCount = result.data.rowCount;
                    this.totalPages = Math.ceil(this.totalRecordsCount / rowCount);
                    if (result.data.rowCount < rowCount) {
                        this.pageSize = result.data.rowCount;
                        this.hidePager = true;
                    } else {
                        this.hidePager = false;
                    }
                } else {
                    this.hidePager = true;
                }
                if (result.data.rowCountAdditional) {
                    this.rowCountAdditional = result.data.rowCountAdditional;
                }
                this.data.records = library.cloneObj(result.data.records);
                this.data.summary = library.cloneObj(result.data.summary);
            }
            this.prepareData();
            this.reloadingData = false;
            if (typeof this.loadFunc === 'function') this.loadFunc(this);
            this.$nextTick(() => {
                this.resizeHeight();
                library.hideLoadingIndicator(this.cId + '_wrapper');
            });
        },
        async gridTypeChange(event) {
            if (this.isList) return;
            let selectedGridType = false;
            if (event && typeof event.value !== 'undefined') {
                selectedGridType = event.value;
            }
            if (selectedGridType !== false && this.selectedGridType !== selectedGridType) {
                await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_AFF_TYPE': selectedGridType });
                this.reloadGrid();
            }
        },
        async gridChange(event) {
            if (this.isList) return;
            let selectedGrid = false;
            if (event && typeof event.value !== 'undefined') {
                selectedGrid = event.value;
            }
            if (selectedGrid !== false && this.selectedGrid !== selectedGrid) {
                await configStore.setGridSettings(this.psp, { 'CLIENT_GRID_SAVED_ID': selectedGrid });
                this.reloadGrid();
            }
        },
        async filterChange(event) {
            if (this.isList) return;
            let selectedFilter = false;
            if (event && typeof event.value !== 'undefined') {
                selectedFilter = event.value;
            } else if (parseInt(event, 10) > 0) {
                selectedFilter = event;
            }
            if (selectedFilter !== false && this.selectedFilter !== selectedFilter) {
                selectedFilter = selectedFilter || -1;
                await configStore.setGridSettings(this.psp, { 'FILTER_ID': selectedFilter });
                this.reloadGrid();
            }
        },
        async showAll() {
            if (typeof this?.$refs?.[this.ref]?.clearSelection === 'function') this.$refs[this.ref].clearSelection();
            this.selectedItems = [];
            this.$emit('update:propSelectedItems', []);
            library.showLoadingIndicator(this.cId + '_wrapper');
            this.groupingColumns = {};
            let params = {
                'CLIENT_GRID_SAVED_ID': null,
                'CLIENT_GRID_AFF_TYPE': -1,
                'FILTER_ID': -1
            };
            await configStore.setGridSettings(this.psp, params);
            await this.gridSearch(true);
        },
        openGridSettings() {
            this.doAction('customizeGrid', { recordId: this.selectedGrid || -1 });
        },
        openFilters() {
            this.doAction('filter');
        },
        async doAction(act, params) {
            if (!params) params = {};
            params.openInSameWindow = true;
            params.psp = this.psp;
            params.isMainGrid = true;
            params.actVal = act;
            if (act === 'customizeGrid') {
                params.windowName = 'CustomizeGrid';
            }
            if (act === 'clipboard') {
                this.clipboardValue = params.text;
                this.$nextTick(() => {
                    this.$refs['clipboard__' + this.psp].select();
                    document.execCommand("copy");
                });
                return;
            }
            /* check section */
            if (params.recordId) {
                this.selectedItems = [params.recordId];
            }
            params.recordId = this.selectedItems;
            switch (act) {
                case 'delete':
                    params._this = this;
                    params.functionOnSuccess = (response) => {
                        this.getGridData(true);
                    };
                    break;
                case 'copy':
                    params._this = this;
                    params.functionOnSuccess = (response) => {
                        if (params.recordId.length > 1) {
                            this.getGridData(true);
                        } else {
                            this.doAction('edit', { recordId: response.data[0] });
                        }
                    };
                    break;
                case 'filter':
                    if (typeof params.dialogSettings === 'undefined') params.dialogSettings = {};
                    params.dialogSettings.cssClass = "show-only-content-popup iframe-popup";
                    break;
            }
            params.doLocalAction = this.doLocalAction;
            params.needResponse = true;
            let tmpActName = 'open' + library.capitalizeFirstLetter(act);
            let tmpActNameEntity = act + 'Entity';
            /* end popup width section */
            if (act.indexOf('HelpMenu') !== -1) {
                await actionStore.openHelpMenu(params);
            } else if (typeof actionStore[tmpActName] === 'function') {
                await actionStore[tmpActName](params);
            } else if (typeof actionStore[tmpActNameEntity] === 'function') {
                await actionStore[tmpActNameEntity](params);
            } else {
                await actionStore.actions.iframeProcessSubmit(act, params, params.width, params.height);
            }
        },
        async doLocalAction(act, params) {
            let reloadGrid = false;
            switch (act) {
                case 'customizeGrid':
                    reloadGrid = true;
                    if (typeof params?.document?.head?.innerHTML !== 'undefined' && params?.document?.head?.innerHTML.indexOf('navigation_reloadMainGrid') === -1) {
                        reloadGrid = false;
                    }
                    break;
                case 'customizeSort':
                    reloadGrid = true;
                    break;
                case 'filter':
                    if (typeof params?.actionParams === 'string') {
                        let tmpParams = params.actionParams.split(',');
                        switch (tmpParams[0]) {
                            case "useFilter":
                                // eslint-disable-next-line no-case-declarations
                                let filterId = parseInt(tmpParams[1], 10);
                                if (filterId) {
                                    this.selectedFilter = null;
                                    this.filterChange(filterId);
                                    reloadGrid = false;
                                }
                                break;
                        }
                    }
                    break;
            }
            if (reloadGrid) {
                await configStore.getGridSettings(this.psp, this.selectedGrid, true);
                this.reloadGrid();
            }
        },
        reloadGrid() {
            this.getMainGridData();
        },
        goToPage: function (pageIndex) {
            if (typeof pageIndex === 'object' && this.$refs['pageInput'] && this.$refs['pageInput'].value && parseInt(this.$refs['pageInput'].value, 10)) {
                pageIndex = parseInt(this.$refs['pageInput'].value, 10);
            }
            if (pageIndex >= 1 && pageIndex <= this.totalPages && pageIndex !== this.currentPage) {
                this.currentPage = pageIndex;
                if (typeof this.changePageFnc === 'function') {
                    this.config.pageSettings.currentPage = event.currentPage;
                    this.changePageFnc(pageIndex);
                }
                this.getGridData();
            }
        }
    },
    components: {
        'ejs-grid': defineAsyncComponent(() => import('@common/syncfusion/grid')),
        'e-columns': ColumnsDirective,
        'e-column': ColumnDirective,
        'e-aggregates': AggregatesDirective,
        'e-aggregate': AggregateDirective,
        'ejs-tooltip': defineAsyncComponent(() => import('@common/syncfusion/tooltip'))
    }
};

componentStore.initComponent(gridMainCmp, gridMainConfig, '@common/grid/grid.vue');
export { gridMainConfig, gridMainCmp };
export default gridMainCmp;
</script>
<style scoped>
:deep(#gridControls) {
    width: 100%;
    overflow: hidden;
}
:deep(.e-grid .e-rowcell .e-checkbox-wrapper),
:deep(.e-grid .e-groupcaption .group-checkbox){
    margin-left: 6px !important;
}
:deep(.e-headercell.e-defaultcursor) {
    padding-left: 0px !important;
}
:deep(.isMainGrid [name^="SEARCH"]) {
    margin-left: -5px;
}
:deep(.isMainGrid .e-sortnumber) {
    margin-right: 15px;
    color: white;
    font-size: 12px;
    font-weight: 400;
}
:deep(.isMainGrid .e-sortnumber:hover) {
    color: #23527c;
    text-decoration: underline;
}
:deep(.e-groupcaption), :deep(.e-summarycell) {
    border-right-width: 1px !important;
    border-right-style: dashed !important;
    padding: 0 5px !important;
    font-weight: 400 !important;
}
:deep(.e-grouptopleftcell-group-ungroup-all) {
    padding-left: 0 !important;
}
:deep(.e-grouptopleftcell .e-emptycell) {
    display: none;
}
:deep(.expanded > .group-expand-all) {
    display: none;
}
:deep(.collapsed > .group-collapse-all) {
    display: none;
}
</style>
<style>

.e-grid .e-gridheader .e-sortfilter .e-headercelldiv,
.e-grid .e-gridheader .e-stackedheadercelldiv,
.e-grid .e-gridheader .e-headercell .e-headercelldiv.e-headerchkcelldiv {
    padding: 0 5px 0 5px;
}

.e-grid .custom-search-column .e-sortnumber,
.e-grid .custom-search-column .e-sortfilterdiv {
    display: none;
}

.e-grid .e-headercell {
    padding: 0px 5px;
}

.e-grid .e-rowcell {
    padding: 5px 5px;
}

.e-grid .e-sortfilterdiv {
    margin: -15px 5px;
}

/*
.e-grid .e-altrow {
    background: #f9f9f9;
}
*/

.number-filter, .date-filter {
    display: flex;
}

.number-filter > div, .date-filter > div {
    flex-grow: 5;
    margin: 0 3px;
}

[id^=grid_mainGrid] .e-filterbarcell:first-child {
    padding: 0px !important;
}

.e-checkbox-wrapper .e-frame, .e-css.e-checkbox-wrapper .e-frame {
    width: 14px;
    height: 14px;
    line-height: 7px;
    font-size: 7px;
}

/*
.e-row .e-checkbox-wrapper, .e-grid .e-headerchkcelldiv {
    margin: 0 auto !important;
    width: 28px;
    height: 28px;
}
*/

.e-grid .e-checkbox-wrapper {
    margin: 0 auto !important;
    width: 28px;
    height: 20px;
    padding: 0 !important;
}

.vms-affiliation-grid-wrapper .e-grid .e-columnheader .e-checkbox-wrapper {
    margin-top: 7px !important;
}

.e-grid .e-headerchkcelldiv {
    margin: -5px !important;
}

.e-grid .e-focused:not(.e-menu-item) {
    box-shadow: unset !important;
}

/*
.e-rowcell.e-gridchkbox, .e-headercell.e-defaultcursor, .e-grid .e-headercell, .e-grid .e-detailheadercell {
    padding: 5px !important;
    padding-left: 16px !important;
}
*/
.e-grid .e-headercell, .e-grid .e-detailheadercell, .e-grid .e-rowcell, .e-grid .e-headerchkcelldiv {
    padding: 0 5px !important;
}

.e-headercell.e-defaultcursor {
    padding-left: 14px !important;
}

.e-input-group-icon.e-date-icon, *.e-control-wrapper .e-input-group-icon.e-date-icon {
    margin-bottom: 2px;
}

.e-ddl.e-popup.e-block.e-lib.e-control.e-popup-open[id*='_gridMain_'] {
    min-width: 180px;
}

.isMainGrid {
    width: 100%;
    height: 100%;
}

.isMainGrid .e-grid, .isMainGrid .e-gridpager, .isMainGrid .e-gridheader {
    border: none !important;
}

.isMainGrid .e-gridpager {
    min-width: 900px;
}

.isMainGrid .e-gridpager > div {
    display: none;
}

.isMainGrid .e-gridpager > div:nth-child(2) {
    display: block;
}

.isMainGrid .grid-navigator.control-section {
    float: left;
    margin-left: 0px;
}

.isMainGrid .grid-sg-list,
.isMainGrid .grid-filter-list {
    width: 520px;
}

.isMainGrid .grid-show-all,
.isMainGrid .grid-filters {
    width: 65px;
}

.isMainGrid .grid-settings {
    width: 90px;
}

.isMainGrid .grid-show-all,
.isMainGrid .grid-settings,
.isMainGrid .grid-sg-list,
.isMainGrid .grid-filters,
.isMainGrid .grid-filter-list {
    display: block;
    float: left;
    margin: 5px;
}

.isMainGrid .e-row {
    background-color: #f9f9f9;
    font-size: 13px;
    font-weight: 400;
}

.isMainGrid .e-row * {
    font-size: 13px;
    font-weight: 400;
}

.isMainGrid .e-altrow {
    background-color: #fff;
}

.isMainGrid .e-row:hover {
    background-color: rgb(254,255,234);
}

.isMainGrid .e-row:hover td {
    background-color: transparent !important;
}

.isMainGrid .e-columnheader  * {
    background-color: #607D8B !important;
    color: #fff;
}

.isMainGrid .e-selectionbackground {
    color: #303030 !important;
    background-color: #e5f7fe !important;
}

.isMainGrid .grid-wrapper {
    margin: 0 5px;
}

.isMainGrid.isForProfessions .grid-wrapper {
    margin: 0 10px;
    border: 1px solid white;
    overflow: hidden;
}

.isForProfessions .e-gridpager {
    display: none;
}

.isMainGrid .e-columnheader .e-frame {
    color: #fff !important;
    border-color: #fff !important;
}

.isForProfessions .e-columnheader .e-frame {
    color: black !important;
    border-color: black !important;
}

.isMainGrid .e-row, .isMainGrid .e-rowcell {
    height: 20px !important;
}

.isMainGrid .e-rowcell,
.isMainGrid .e-filterbarcell {
    border-right: 1px dashed #d6d6d6 !important;
}
.isMainGrid .e-filterbarcell input{
    height: 18px !important;
}

.isMainGrid .e-headercelldiv {
    text-align: center !important;
}

.isMainGrid button {
    font-family: "Open Sans","Helvetica Neue",Helvetica,Arial,sans-serif!important;
    background: #e0e0e0!important;
    color: #303030!important;
    border: 1px solid #bdbdbd!important;
    border-radius: 0!important;
    font-weight: 500!important;
    padding: 3px 5px!important;
    text-align: center!important;
    vertical-align: baseline!important;
    width: auto !important;
    height: 26px !important;
    transition: background-color 0.23s ease-in-out 0s !important;
    box-shadow: none !important;
    font-size: 13px !important;
    margin: 0 auto;
}

.isMainGrid .e-headertext {
    font-size: 13px;
    font-weight: 400 !important;
    color: #fff !important;
    text-decoration: none !important;
}

.isMainGrid input {
    line-height: 14px !important;
    font-size: 13px !important;
    min-height: 14px !important;
}

.isMainGrid #grid-main-content-inner .e-spinner-pane {
    background-color: #5a5a5a;
    opacity: 0.25;
}

.isMainGrid .e-defaultcursor, .isMainGrid .e-rhandler.e-rcursor {
    height: 36px !important
}

.isMainGrid [id^='clipboard__']{
    width: 1px;
    height: 1px;
    position: absolute;
    top: -100px;
}

.isMainGrid .e-grid .e-filterbarcell, .isMainGrid .e-grid .e-filterbarcelldisabled {
    padding-left: 5px !important;
    padding-right: 5px !important;
}

.isMainGrid .e-sortfilterdiv.e-icons {
    color: white !important;
}

.isMainGrid .e-pagertemplatemessage {
    width: 400px;
    float: left;
    line-height: 27px;
}
.isMainGrid.isForProfessions .e-pagertemplatemessage {
    width: 290px;
}

.isMainGrid .e-emptyrow {
    visibility: hidden;
}

.isMainGrid .grid-navigator {
    padding-bottom: 0px;
}

.isForProfessions .grid-navigator {
    color: white;
}

.isForProfessions .control-section {
    display: none;
}

.isForProfessions .e-columnheader * {
    background-color: #f2f3f7 !important;
    color: black !important;
}

[id=foldersManager_mainGrid_ACTIONS] {
    color: #007bdb !important;
}

[id=grid_mainGrid__COORD_PRO_OPP] {
    width: 250px!important;
    padding: 0 10px 0 0!important;
}

.proPlace {
   margin-right: 50px;
   align-items: center;
}

.proPlace > * {
    valign: center;
    padding: 3px 15px 5px 0px!important;
}

.proPlace [type="button"] {
    width: 57px!important;
    height: 27px!important;
    border-color: #858585 !important;
    background-color: #E0E0E0 !important;
}

.isMainGrid .e-gridpager {
    display: none;
}

.e-cloneproperties.e-draganddrop {
    z-index: 9999999 !important;
    position: fixed !important;
}
.e-cclist .e-checkbox-wrapper {
    width: 100%;
}
.e-cclist .e-checkbox-wrapper > * {
    display: inline;
}
.isMainGrid .grid-type {
    width: 280px;
    float: left;
    margin-left: 5px;
}
.isMainGrid .e-grid .e-content {
    overflow-x: hidden !important;
}

.isMainGrid .e-input-group-icon.e-date-icon, .isMainGrid *.e-control-wrapper .e-input-group-icon.e-date-icon {
    margin-bottom: 0px;
}

.summaryGrid {
    display: inline-block;
    z-index: 1;
}

.summaryGrid .infoTitle {
    margin-top: 10px;
    margin-bottom: 5px;
    font-weight: bold;
}

.summaryGrid .infoRow {
    display: inline-block;
    background: #c5d6fc;
    overflow-x: none;
    padding: 5px 0;
}

.summaryGrid .infoCol {
    display: inline-block;
    text-align: right;
    padding-right: 10px;
    margin-left: -10px;
}

</style>
